import React, { useEffect, useState } from 'react';
import { BookOpen, ChevronDown, ChevronUp, Download, ExternalLink, HelpCircle, PlayCircle, Puzzle, ShieldCheck } from 'lucide-react';
import { CASI_TAKATA, FAQ_SEGRETERIA, CasoTakata } from '../../data/takataFaq';

// Guide all'estensione: come si installa e come si usa.
//
// Nasce nella pagina di verifica, dove stava fino al 29/07/2026. Da quando la verifica si
// fa SOLO con l'estensione, il registro è la prima pagina che l'associato vede e le
// istruzioni devono stare lì.
//
// DUE GUIDE SEPARATE E CHIUSE PER DEFAULT: la guida all'uso era sempre aperta e occupava
// mezza pagina a ogni caricamento, anche a chi lavora tutti i giorni e non ne ha bisogno.
// Chi cerca istruzioni le apre; chi cerca il registro lo trova subito sotto.

export const ESTENSIONE_URL = 'https://chromewebstore.google.com/detail/ijgailcapkhgmdhpcdfclfglmeafbhia';

// Rileva l'estensione: quando c'è, marca un attributo su <html>. Il controllo si ripete
// dopo un attimo perché lo script dell'estensione può arrivare dopo il primo disegno.
export function useEstensionePresente(): boolean {
  const [presente, setPresente] = useState(false);
  useEffect(() => {
    const check = () => setPresente(document.documentElement.hasAttribute('data-unasca-takata-ext'));
    check();
    const t = setTimeout(check, 800);
    return () => clearTimeout(t);
  }, []);
  return presente;
}

const Passo: React.FC<{ n: string; verde?: boolean; children: React.ReactNode }> = ({ n, verde, children }) => (
  <li className="flex gap-3">
    <span className={`flex-shrink-0 w-5 h-5 rounded-full text-white text-2xs flex items-center justify-center font-bold ${verde ? 'bg-green-600' : 'bg-indigo-600'}`}>{n}</span>
    <div className="min-w-0">{children}</div>
  </li>
);

const Figura: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    loading="lazy"
    className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 max-w-full"
  />
);

const Nascondi: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="mt-4 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 hover:bg-gray-200 dark:hover:bg-gray-700"
  >
    <ChevronUp className="w-3.5 h-3.5" /> Nascondi
  </button>
);

// Un gruppo di voci FAQ a fisarmonica (una aperta alla volta, condivisa fra i gruppi).
const GruppoFaq: React.FC<{
  titolo: string;
  voci: CasoTakata[];
  casoAperto: string;
  setCasoAperto: (id: string) => void;
}> = ({ titolo, voci, casoAperto, setCasoAperto }) => (
  <div className="mb-3 last:mb-0">
    <h4 className="text-2xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
      {titolo}
    </h4>
    <div className="divide-y divide-gray-100 dark:divide-gray-700">
      {voci.map((c) => (
        <div key={c.id} className="py-2">
          <button
            onClick={() => setCasoAperto(casoAperto === c.id ? '' : c.id)}
            className="w-full flex items-start gap-2 text-left"
          >
            {casoAperto === c.id
              ? <ChevronUp className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
              : <ChevronDown className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />}
            <span className={`text-sm ${casoAperto === c.id ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
              {c.titolo}
            </span>
          </button>
          {casoAperto === c.id && (
            <div className="mt-1.5 ml-5 space-y-1.5">
              {c.corpo.map((p, i) => (
                <p key={i} className="text-sm text-gray-600 dark:text-gray-400">{p}</p>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

interface Props {
  /** Apre la guida all'installazione già espansa (utile su una pagina dedicata). */
  apriSubito?: boolean;
}

const IstruzioniEstensione: React.FC<Props> = ({ apriSubito = false }) => {
  const estensionePresente = useEstensionePresente();
  const [installazione, setInstallazione] = useState(apriSubito);
  const [uso, setUso] = useState(false);
  const [video, setVideo] = useState(false);
  const [casi, setCasi] = useState(false);
  // Il caso aperto dentro «Casi particolari» (uno alla volta anche qui).
  const [casoAperto, setCasoAperto] = useState('');

  // Un pulsante alla volta: aperte tutte, la pagina tornerebbe lunga come prima.
  const apriInstallazione = () => { setInstallazione((v) => !v); setUso(false); setVideo(false); setCasi(false); };
  const apriUso = () => { setUso((v) => !v); setInstallazione(false); setVideo(false); setCasi(false); };
  const apriVideo = () => { setVideo((v) => !v); setInstallazione(false); setUso(false); setCasi(false); };
  const apriCasi = () => { setCasi((v) => !v); setInstallazione(false); setUso(false); setVideo(false); };

  return (
    <>
      {/* Stato: una riga sola. Il richiamo a installare resta forte finché serve. */}
      {!estensionePresente ? (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4 mb-3">
          <div className="flex items-start gap-3">
            <Puzzle className="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                Installa l'estensione per verificare i telai
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                La verifica si fa con l'estensione: inserisci il numero di telaio e lei apre il portale
                ufficiale del costruttore, lo compila, legge l'esito e registra qui la verifica con la
                schermata da allegare al certificato.
              </p>
              <a
                href={ESTENSIONE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
              >
                <ExternalLink className="w-4 h-4" /> Installa dal Chrome Web Store
              </a>
              <p className="text-2xs text-gray-500 dark:text-gray-400 mt-2">
                Funziona con Google Chrome e Microsoft Edge. Serve il tuo account UNASCA: sei già collegato.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-4 py-2.5 mb-3">
          <ShieldCheck className="w-4 h-4 text-green-700 dark:text-green-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-green-800 dark:text-green-300">
            Estensione installata e collegata
          </span>
        </div>
      )}

      {/* I quattro pulsanti: sempre disponibili, guide chiuse finché non servono. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <button
          onClick={apriInstallazione}
          className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
            installazione
              ? 'bg-indigo-600 border-indigo-600 text-white'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-indigo-400'
          }`}
        >
          <Download className="w-4 h-4" /> Istruzioni installazione
          {installazione ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={apriUso}
          className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
            uso
              ? 'bg-green-600 border-green-600 text-white'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-green-400'
          }`}
        >
          <BookOpen className="w-4 h-4" /> Guida all'uso
          {uso ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={apriVideo}
          className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
            video
              ? 'bg-red-600 border-red-600 text-white'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-red-400'
          }`}
        >
          <PlayCircle className="w-4 h-4" /> Video guida
          {video ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={apriCasi}
          className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
            casi
              ? 'bg-amber-600 border-amber-600 text-white'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-amber-400'
          }`}
        >
          <HelpCircle className="w-4 h-4" /> FAQ
          {casi ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* ─── FAQ ──────────────────────────────────────────────────────────── */}
      {/* Le voci vivono in data/takataFaq.ts e si aggiornano con la skill
          `aggiorna-casi-takata`: ogni caso nuovo dal campo va aggiunto lì, mai qui.
          Due gruppi: i casi operativi dello strumento e le FAQ ufficiali della
          Segreteria Nazionale sul Decreto 229/2026. Fisarmonica unica (casoAperto):
          gli id dei due gruppi non collidono (i secondi hanno prefisso sns-). */}
      {casi && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-4">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">FAQ</h3>
          <p className="text-2xs text-gray-500 dark:text-gray-400 mb-3">
            Le situazioni fuori dall'ordinario segnalate dagli studi, e le risposte ufficiali
            della Segreteria Nazionale sul Decreto 229/2026. L'elenco cresce con le vostre
            segnalazioni.
          </p>
          <GruppoFaq
            titolo="Lo strumento e i casi segnalati dagli studi"
            voci={CASI_TAKATA}
            casoAperto={casoAperto}
            setCasoAperto={setCasoAperto}
          />
          <GruppoFaq
            titolo="Le risposte della Segreteria Nazionale (Decreto 229/2026)"
            voci={FAQ_SEGRETERIA}
            casoAperto={casoAperto}
            setCasoAperto={setCasoAperto}
          />
          <Nascondi onClick={() => setCasi(false)} />
        </div>
      )}

      {/* ─── Video guida ──────────────────────────────────────────────────── */}
      {/* Il caricamento avviene solo all'apertura (il pannello chiuso non è nel DOM), e il
          dominio nocookie evita i cookie di profilazione YouTube finché non si preme Play. */}
      {video && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-4">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
            Installazione e prima verifica, in video
          </h3>
          <p className="text-2xs text-gray-500 dark:text-gray-400 mb-3">
            Tutto il percorso in pochi minuti: installazione dallo store, collegamento, prima
            verifica e certificato. I dettagli passo per passo restano nelle due guide scritte.
          </p>
          <div className="aspect-video w-full rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <iframe
              className="w-full h-full"
              src="https://www.youtube-nocookie.com/embed/ZleNrOAEd4E"
              title="Verifica richiamo airbag Takata — installazione e uso dell'estensione UNASCA"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
          <Nascondi onClick={() => setVideo(false)} />
        </div>
      )}

      {/* ─── Guida: installazione ─────────────────────────────────────────── */}
      {installazione && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-4">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Installazione e primo avvio</h3>
          <ol className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
            <Passo n="1">
              Apri il <a href={ESTENSIONE_URL} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 underline">link allo Chrome Web Store</a> e
              premi <strong>Aggiungi</strong> in alto a destra.
            </Passo>
            <Passo n="2">
              <strong>Può comparire un avviso «Procedi con cautela»</strong>, che dice che
              l'estensione «non è ritenuta attendibile da Navigazione sicura avanzata». Premi
              <strong> Installa comunque</strong>.
              <Figura src="/images/estensione/chrome-procedi-cautela.png" alt="Avviso di Chrome: Procedi con cautela" />
              <span className="block mt-2 text-2xs text-gray-500 dark:text-gray-400">
                <strong>Perché compare.</strong> Chrome considera «attendibile» un'estensione dopo che
                è stata sullo store abbastanza a lungo e con abbastanza installazioni. La nostra è
                appena pubblicata e, per scelta, <strong>non è elencata pubblicamente</strong>: si
                installa solo da questo indirizzo, riservato agli associati. L'avviso dice che Chrome
                non ha ancora una storia su cui basarsi, non che ci sia qualcosa di sospetto. Sparirà
                con il tempo.
              </span>
            </Passo>
            <Passo n="3">
              Chrome chiede conferma: premi <strong>Aggiungi estensione</strong>.
              <Figura src="/images/estensione/chrome-aggiungi.png" alt="Finestra di Chrome: Aggiungere UNASCA - Cattura verifica Takata?" />
            </Passo>
            <Passo n="4">
              Un riquadro conferma che l'estensione è stata aggiunta, e compare l'icona con lo
              <strong> scudo verde</strong> accanto alla barra degli indirizzi.
              <Figura src="/images/estensione/chrome-aggiunta.png" alt="Conferma di Chrome: l'estensione è stata aggiunta" />
            </Passo>
            <Passo n="5">
              Nel pannello premi <strong>«Consenti la cattura della prova»</strong> e conferma con
              <strong> Consenti</strong>: è il permesso che serve a fotografare la schermata dell'esito
              da allegare al certificato. Senza, le verifiche vengono registrate ma senza la prova.
              <Figura src="/images/estensione/chrome-permesso-cattura.png" alt="Finestra di Chrome che chiede il permesso di leggere i dati sui siti" />
            </Passo>
            <Passo n="✓" verde>
              Quando il pannello mostra in verde <strong>«Collegato»</strong> con il nome del tuo studio
              sei pronto.
              {/* Chrome non attiva l'estensione nelle schede GIÀ aperte: il collegamento avviene
                  quando la pagina viene caricata con l'estensione installata. Detto solo qui,
                  a chi incontra il problema, invece che come passaggio per tutti. */}
              <span className="block mt-1 text-2xs text-gray-500 dark:text-gray-400">
                Se dice «Non collegato», ricarica questa pagina (F5) e riapri il pannello.
              </span>
            </Passo>
          </ol>

          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-2xs text-gray-500 dark:text-gray-400">
              <strong>Sui permessi che Chrome mostra.</strong> «Leggere e modificare i tuoi dati su una serie
              di siti» sono i portali dei costruttori, dove l'estensione compila il telaio e legge l'esito.
              «Leggere la cronologia di navigazione» è il nome che Chrome dà al permesso sulle schede: serve
              ad aprire il portale nella scheda giusta e a riportarti al registro a verifica conclusa — la
              cronologia non viene letta. L'informativa completa è{' '}
              <a href="/estensione-takata-privacy/" target="_blank" rel="noopener noreferrer" className="underline">
                a questo indirizzo
              </a>.
            </p>
            <p className="text-2xs text-gray-500 dark:text-gray-400 mt-2">
              <strong>Attenzione:</strong> UNASCA non ti chiederà mai di installare estensioni ricevute per
              email o messaggio. L'unico indirizzo valido è quello del pulsante qui sopra.
            </p>
          </div>

          <Nascondi onClick={() => setInstallazione(false)} />
        </div>
      )}

      {/* ─── Guida: uso ───────────────────────────────────────────────────── */}
      {uso && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-4">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Come avviare una verifica</h3>
          <ol className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
            <Passo n="1" verde>
              Fai clic sullo <strong>scudo verde</strong> in alto a destra, accanto alla barra degli
              indirizzi. Se non lo vedi, apri il menù delle estensioni (l'icona a forma di tessera
              di puzzle) e <strong>fissalo</strong> con la puntina: da lì in poi resta sempre in vista.
              <Figura src="/images/estensione/chrome-fissa-estensione.png" alt="Menù delle estensioni di Chrome con la puntina per fissare l'estensione UNASCA" />
            </Passo>
            <Passo n="2" verde>
              Scrivi il <strong>numero di telaio</strong>. Se sei già su una pagina che lo mostra,
              premi <strong>RILEVA</strong> e lo prende da lì. La marca compare da sola: se il telaio
              non basta a riconoscerla, <strong>sceglila dal menù</strong>.
              <Figura src="/images/estensione/pannello-telaio.png" alt="Pannello dell'estensione con il campo del numero di telaio evidenziato" />
            </Passo>
            <Passo n="3" verde>
              Se vuoi, aggiungi <strong>numero di pratica</strong> e <strong>corrispettivo</strong>:
              finiscono sul certificato, e la pratica anche davanti al nome del file scaricato.
            </Passo>
            <Passo n="4" verde>
              Premi <strong>«Apri portale e compila telaio»</strong>.
            </Passo>
            <Passo n="✓" verde>
              A verifica conclusa il <strong>certificato si apre da solo</strong> e la riga compare
              qui nel registro, dove puoi completare i dati dell'acquirente.
            </Passo>
          </ol>

          {/* Il caso che conta davvero: veicolo coinvolto, dichiarazione da far firmare.
              Il pulsante con la matita non si spiega da solo, e chi non lo apre consegna al
              cliente una dichiarazione senza i suoi dati. */}
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2">
              Se il veicolo è COINVOLTO: i dati dell'acquirente
            </h4>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Per i veicoli coinvolti il certificato contiene anche la <strong>dichiarazione di presa
              visione</strong> da far firmare a chi acquista. Per completarla con i suoi dati, nel registro
              qui sotto premi la <strong>matita</strong> sulla riga della verifica: si apre un pannello dove
              indicare nome, cognome, luogo e data di nascita, codice fiscale e residenza — oppure la
              <strong> società</strong>, se a firmare è il legale rappresentante. Da lì si possono
              aggiungere anche <strong>numero di pratica</strong> e <strong>corrispettivo</strong>, se non
              li avevi già scritti nel pannello dell'estensione.
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
              Premi <strong>«Salva dati e aggiorna dichiarazione»</strong> e riscarica il documento: i dati
              compariranno nella dichiarazione. Puoi tornarci quando vuoi, anche a distanza di giorni.
            </p>
            <Figura
              src="/images/estensione/registro-dati-acquirente.png"
              alt="Riga del registro aperta con la matita: riferimenti della pratica e dati dell'acquirente"
            />
          </div>

          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Cosa aspettarsi, marca per marca</h4>
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex gap-2">
                <span className="text-green-600 mt-0.5">{'•'}</span>
                <span>
                  <strong>Con portale</strong> (Citroën, Peugeot, Opel, Seat, Ford, Toyota…): si apre il
                  sito della casa, il telaio viene compilato. A te resta il codice di sicurezza, dove c'è.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-green-600 mt-0.5">{'•'}</span>
                <span>
                  <strong>Verificate dal server</strong> (Nissan, Mitsubishi, Daihatsu, Subaru, Jeep,
                  Lancia, Dodge, Ram, Chrysler): non si apre nulla, il certificato arriva da sé. Se il
                  codice di sicurezza non viene letto, si apre una scheda dove inserirlo.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-green-600 mt-0.5">{'•'}</span>
                <span>
                  <strong>Fiat, Alfa Romeo, Abarth e Maserati</strong> rispondono all'istante: non
                  rientrano nell'elenco ministeriale delle marche coinvolte.
                </span>
              </li>
            </ul>
            <p className="text-2xs text-gray-500 dark:text-gray-400 mt-3">
              Sei già sulla pagina d'esito di un portale e vuoi solo registrarla? Apri il pannello e usa
              <strong> «Cattura da questa pagina»</strong>. Se riapri il pannello mentre una verifica è in
              corso, ti dice a che punto è.
            </p>
          </div>

          <Nascondi onClick={() => setUso(false)} />
        </div>
      )}
    </>
  );
};

export default IstruzioniEstensione;
